import React, { useCallback, useMemo } from 'react'

import {
  AppBar,
  Authenticator,
  CircularProgressCenter,
  Drawer,
  FireCMS,
  ModeControllerProvider,
  NavigationRoutes,
  Scaffold,
  SideDialogs,
  SnackbarProvider,
  useBuildLocalConfigurationPersistence,
  useBuildModeController,
  useBuildNavigationController,
  useValidateAuthenticator
} from '@firecms/core'
import {
  FirebaseAuthController,
  FirebaseLoginView,
  FirebaseSignInProvider,
  FirebaseUserWrapper,
  useFirebaseAuthController,
  useFirebaseStorageSource,
  useFirestoreDelegate,
  useInitialiseFirebase
} from '@firecms/firebase'
import { CenteredView } from '@firecms/ui'

import { recipesCollection } from './collections/recipes';

import { firebaseConfig } from './firebase_config'

function App() {
  // Use your own authentication logic here
  const myAuthenticator: Authenticator<FirebaseUserWrapper> = useCallback(
    async ({ user, authController }) => {
      // 1. If no user found, deny access immediately.

      console.log("Logged in user UID:", user?.uid);
      
      if (!user?.firebaseUser) {
        throw new Error("No Firebase user found");
      }
  
      // 2. Force a token refresh to grab the latest custom claims.
      const idTokenResult = await user.firebaseUser.getIdTokenResult(true);
  
      // 3. Retrieve the 'role' claim from the token.
      const role = idTokenResult.claims?.role;
  
      // 4. Check if the user is an admin. If not, throw to deny.
      if (role !== "admin") {
        throw new Error("You do not have admin permissions!");
      }
  
      console.log("Allowing access to", user.email, "with role:", role);
      return true; // Access granted
    },
    []
  );
  

  const collections = useMemo(() => [recipesCollection], [])

  const { firebaseApp, firebaseConfigLoading, configError } = useInitialiseFirebase({
    firebaseConfig
  })

  // Controller used to manage the dark or light color mode
  const modeController = useBuildModeController()

  const signInOptions: FirebaseSignInProvider[] = ['google.com']

  // Controller for managing authentication
  const authController: FirebaseAuthController = useFirebaseAuthController({
    firebaseApp,
    signInOptions
  })

  // Controller for saving some user preferences locally.
  const userConfigPersistence = useBuildLocalConfigurationPersistence()

  // Delegate used for fetching and saving data in Firestore
  const firestoreDelegate = useFirestoreDelegate({
    firebaseApp
  })

  // Controller used for saving and fetching files in storage
  const storageSource = useFirebaseStorageSource({
    firebaseApp
  })

  const { authLoading, canAccessMainView, notAllowedError } = useValidateAuthenticator({
    authController,
    authenticator: myAuthenticator,
    dataSourceDelegate: firestoreDelegate,
    storageSource
  })

  const navigationController = useBuildNavigationController({
    collections,
    authController,
    dataSourceDelegate: firestoreDelegate
  })

  if (firebaseConfigLoading || !firebaseApp) {
    return (
      <>
        <CircularProgressCenter />
      </>
    )
  }

  if (configError) {
    return <CenteredView>{configError}</CenteredView>
  }

  return (
    <SnackbarProvider>
      <ModeControllerProvider value={modeController}>
        <FireCMS
          navigationController={navigationController}
          authController={authController}
          userConfigPersistence={userConfigPersistence}
          dataSourceDelegate={firestoreDelegate}
          storageSource={storageSource}
          {...({ telemetry: { disabled: true } } as any)}
         
        >
          {({ context, loading }) => {
            if (loading || authLoading) {
              return <CircularProgressCenter size={'large'} />
            }

            if (!canAccessMainView) {
              return (
                <FirebaseLoginView
                  authController={authController}
                  firebaseApp={firebaseApp}
                  signInOptions={signInOptions}
                  notAllowedError={notAllowedError}
                />
              )
            }

            return (
              <Scaffold autoOpenDrawer={false}>
                <AppBar title={'JDE Recipes CMS'} />
                <Drawer />
                <NavigationRoutes />
                <SideDialogs />
              </Scaffold>
            )
          }}
        </FireCMS>
      </ModeControllerProvider>
    </SnackbarProvider>
  )
}

export default App
